import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaBrain, FaUsers, FaTrophy, FaPlay, FaClock, FaStar } from 'react-icons/fa';
import axios from 'axios';
import './PublicQuizDetails.css';
import AppDownloadBanner from './AppDownloadBanner';

function PublicQuizDetails() {
  const { quizId } = useParams();
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuizDetails = async () => {
      try {
        setLoading(true);
        
        // Créer une instance axios sans withCredentials pour les appels publics
        const publicAxios = axios.create({
          withCredentials: false,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        // Récupérer les détails du quiz (appel public)
        const response = await publicAxios.get(`/api/quizzes/${quizId}`);
        setQuizData(response.data);
        
      } catch (error) {
        console.error('Error fetching quiz details:', error);
        setError('Impossible de charger les détails de ce quiz');
      } finally {
        setLoading(false);
      }
    };

    if (quizId) {
      fetchQuizDetails();
    }
  }, [quizId]);

  // Fonctions simples de détection de plateforme
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = () => /Android/.test(navigator.userAgent);
  const isDesktop = () => !isIOS() && !isAndroid();

  // Rendu du bouton selon la plateforme
  const renderAppButton = () => {
    if (isIOS() || isAndroid()) {
      return (
        <a href={`cinemapp://quiz/${quizId}`} className="prominent-app-button">
          📱 Ouvrir dans CinéList
        </a>
      );
    } else if (isDesktop()) {
      return (
        <button 
          className="prominent-app-button desktop-download" 
          onClick={() => {
            alert('📱 Téléchargez CinéList sur votre mobile !\n\n🍎 iOS: App Store\n🤖 Android: Play Store');
          }}
        >
          📲 Télécharger CinéList
        </button>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="public-quiz-page">
        <div className="loading-container">
          <h2>Chargement du quiz...</h2>
        </div>
      </div>
    );
  }

  if (error || !quizData) {
    return (
      <div className="public-quiz-page">
        <AppDownloadBanner quizId={quizId} />
        <div className="error-container">
          <h2>Quiz non disponible</h2>
          <p>{error || 'Ce quiz n\'est pas accessible.'}</p>
          <div className="download-suggestion">
            <h3>Jouez à ce quiz dans l'app CinéList</h3>
            <p>Téléchargez l'app pour accéder aux quizzes complets, suivre vos scores et défier vos amis !</p>
          </div>
        </div>
      </div>
    );
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'facile':
      case 'easy':
        return '#28a745';
      case 'moyen':
      case 'medium':
        return '#ffc107';
      case 'difficile':
      case 'hard':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const questionCount = quizData.questions ? quizData.questions.length : 0;
  const userScore = quizData.user_score || 0;
  const hasCompleted = quizData.user_has_completed || false;

  return (
    <div className="public-quiz-page">
      <Helmet>
        <meta 
          name="apple-itunes-app" 
          content={`app-id=6740208004, app-argument=https://films.ahkapp.fr/quiz/${quizId}`} 
        />
        <meta 
          property="og:title" 
          content={quizData ? `${quizData.title} - CinéList` : 'Quiz CinéList'} 
        />
        <meta 
          property="og:description" 
          content={quizData ? `Testez vos connaissances avec le quiz "${quizData.title}" (${quizData.difficulty}) sur CinéList !` : 'Découvrez ce quiz sur CinéList'} 
        />
        <meta 
          property="og:image" 
          content={quizData && quizData.image_url 
            ? quizData.image_url 
            : 'https://films.ahkapp.fr/logo512.png'
          } 
        />
        <meta 
          property="og:image:width" 
          content="400" 
        />
        <meta 
          property="og:image:height" 
          content="400" 
        />
        <meta 
          property="og:image:type" 
          content="image/jpeg" 
        />
        <meta 
          property="og:url" 
          content={`https://films.ahkapp.fr/quiz/${quizId}`} 
        />
        <meta 
          property="og:type" 
          content="article" 
        />
      </Helmet>
      
      <AppDownloadBanner quizId={quizId} />
      
      <div className="quiz-hero">
        <div className="quiz-image-section">
          {quizData.image_url ? (
            <img 
              src={quizData.image_url}
              alt={quizData.title}
              className="quiz-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/400x300?text=Quiz';
              }}
            />
          ) : (
            <div className="placeholder-quiz-image">
              <FaBrain size={80} />
              <p>Quiz</p>
            </div>
          )}
        </div>

        {/* Section bouton central entre image et titre */}
        <div className="app-button-section">
          {renderAppButton()}
        </div>

        <div className="quiz-info">
          <h1>{quizData.title}</h1>
          
          <div className="quiz-meta">
            <div className="meta-item">
              <FaBrain />
              <span>{quizData.theme || 'Quiz'}</span>
            </div>
            
            <div className="meta-item difficulty" style={{ color: getDifficultyColor(quizData.difficulty) }}>
              <FaStar />
              <span>{quizData.difficulty || 'Non spécifié'}</span>
            </div>
            
            <div className="meta-item">
              <FaClock />
              <span>{questionCount} questions</span>
            </div>
          </div>

          {hasCompleted && (
            <div className="user-score-section">
              <div className="score-badge">
                <FaTrophy />
                <span>Votre score : {userScore}/{questionCount}</span>
              </div>
            </div>
          )}

          {quizData.follower_scores && quizData.follower_scores.length > 0 && (
            <div className="friends-scores">
              <h3>Scores de vos amis</h3>
              <div className="friends-grid">
                {quizData.follower_scores.slice(0, 6).map((friend, index) => (
                  <div key={index} className="friend-score">
                    <img 
                      src={friend.avatar_url || 'https://via.placeholder.com/50'} 
                      alt={friend.pseudo}
                      className="friend-avatar"
                    />
                    <div className="friend-info">
                      <span className="friend-name">{friend.pseudo}</span>
                      <span className="friend-score-value">
                        {friend.score}/{friend.total_questions}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="app-promotion-section">
        <div className="app-promo-card">
          <h3>🧠 Jouez à "{quizData.title}" sur CinéList !</h3>
          <div className="promo-features">
            <div className="promo-feature">
              <FaPlay /> Jouez aux quizzes
            </div>
            <div className="promo-feature">
              <FaTrophy /> Suivez vos scores
            </div>
            <div className="promo-feature">
              <FaUsers /> Défiez vos amis
            </div>
          </div>
          <p>
            Téléchargez l'app CinéList pour jouer à ce quiz, 
            comparer vos scores avec vos amis et découvrir de nouveaux défis !
          </p>
        </div>
      </div>

      <div className="limited-access-notice">
        <h4>Version web limitée</h4>
        <p>
          Cette page web ne montre qu'un aperçu. Pour jouer aux quizzes, 
          voir vos scores détaillés et défier vos amis, 
          téléchargez l'app mobile CinéList.
        </p>
      </div>
    </div>
  );
}

export default PublicQuizDetails;